import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";

const LOGO_BLACK = "https://customer-assets.emergentagent.com/job_9bee4b07-8d72-479e-a1c0-b19de3e7f530/artifacts/13f66ozi_biro_logo_black-350x100.png";
const VEHICLE_IMAGES = {
    small: "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/2pb5q7d8_biro-classic-small-b2211_1%20%281%29.jpg",
    big: "https://customer-assets.emergentagent.com/job_urban-mobility-35/artifacts/9kuw3yw5_biro-classic-big-b2211_1.jpg",
};

export default function OfferView() {
    const { id } = useParams();
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [responding, setResponding] = useState(false);

    useEffect(() => {
        api.get(`/offers/${id}`).then((r) => { setOffer(r.data); api.post(`/offers/${id}/view`); }).catch(() => {}).finally(() => setLoading(false));
    }, [id]);

    const respond = async (action) => {
        setResponding(true);
        try { await api.post(`/offers/${id}/respond`, { action }); setOffer((o) => ({ ...o, status: action === "accept" ? "accepted" : "rejected" })); }
        catch {} finally { setResponding(false); }
    };

    if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>;
    if (!offer) return <div className="flex items-center justify-center min-h-screen text-neutral-500">Offert hittades inte</div>;

    const company = offer.company || {};
    const vehicleImg = VEHICLE_IMAGES[offer.vehicle_model] || null;
    const vatLabel = offer.vat_mode === "ex" ? "Exklusive moms" : "Inklusive moms";

    return (
        <div className="min-h-screen bg-white" data-testid="offer-view-page">
            <div className="max-w-2xl mx-auto px-6 py-12">
                {/* Header */}
                <div className="flex items-start justify-between mb-8">
                    <img src={LOGO_BLACK} alt="Birò" className="h-6" />
                    <div className="text-right text-xs text-neutral-400">
                        {company.company_name && <div className="font-medium text-neutral-600">{company.company_name}</div>}
                        {company.org_number && <div>Org.nr: {company.org_number}</div>}
                        {company.address && <div>{company.address}</div>}
                        {company.email && <div>{company.email}</div>}
                        {company.phone && <div>{company.phone}</div>}
                    </div>
                </div>

                <h1 className="text-3xl font-medium tracking-tight mb-2">Offert</h1>
                <p className="text-sm text-neutral-500 mb-8">Till: {offer.contact_name} ({offer.contact_email})</p>

                {/* Vehicle Image */}
                {vehicleImg && (
                    <div className="mb-8 bg-neutral-50 p-8 flex justify-center">
                        <img src={vehicleImg} alt={`Birò ${offer.vehicle_model === "small" ? "Small" : "Big"}`} className="max-h-64 object-contain" />
                    </div>
                )}
                {offer.vehicle_model && (
                    <p className="text-lg font-medium mb-6">Birò {offer.vehicle_model === "small" ? "Small" : "Big"}</p>
                )}

                {/* Line Items */}
                <div className="border border-neutral-200 divide-y divide-neutral-200">
                    <div className="p-4 bg-neutral-50 flex justify-between text-xs uppercase tracking-wider text-neutral-500 font-medium">
                        <span>Beskrivning</span><span>Pris</span>
                    </div>
                    {offer.items?.map((item, i) => (
                        <div key={i} className="p-4 flex justify-between text-sm">
                            <span>{item.description}</span>
                            <span className="font-medium">{item.price?.toLocaleString()} SEK</span>
                        </div>
                    ))}
                    <div className="p-4 flex justify-between font-medium">
                        <span>Totalt <span className="text-xs text-neutral-400 font-normal">({vatLabel})</span></span>
                        <span className="text-lg">{offer.total?.toLocaleString()} SEK</span>
                    </div>
                </div>

                {offer.notes && <p className="mt-4 text-sm text-neutral-500">{offer.notes}</p>}

                {/* Leasing Options */}
                {offer.leasing_options?.length > 0 && (
                    <div className="mt-8">
                        <Separator className="mb-6" />
                        <h3 className="text-lg font-medium mb-4">Leasingalternativ</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {offer.leasing_options.map((lo, i) => (
                                <div key={i} className="border border-neutral-200 p-5" data-testid={`leasing-option-${i}`}>
                                    <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">Alternativ {i + 1}</div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-neutral-500">Löptid</span>
                                            <span className="font-medium">{lo.months} månader</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-neutral-500">Restvärde</span>
                                            <span className="font-medium">{lo.residual_value?.toLocaleString()} SEK</span>
                                        </div>
                                        <Separator />
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-neutral-500">Månadskostnad</span>
                                            <span className="text-xl font-medium">{lo.monthly_cost?.toLocaleString()} SEK/mån</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Accept/Reject */}
                {(offer.status === "sent" || offer.status === "viewed") && (
                    <div className="flex gap-3 mt-8">
                        <Button data-testid="accept-offer-btn" onClick={() => respond("accept")} disabled={responding} className="flex-1 bg-black text-white hover:bg-neutral-800">
                            <Check size={16} className="mr-2" /> Acceptera
                        </Button>
                        <Button data-testid="reject-offer-btn" onClick={() => respond("reject")} disabled={responding} variant="outline" className="flex-1">
                            <X size={16} className="mr-2" /> Avvisa
                        </Button>
                    </div>
                )}
                {offer.status === "accepted" && <div className="mt-8 p-4 bg-green-50 border border-green-200 text-green-800 text-sm text-center">Offerten har accepterats</div>}
                {offer.status === "rejected" && <div className="mt-8 p-4 bg-red-50 border border-red-200 text-red-800 text-sm text-center">Offerten har avvisats</div>}

                {/* Footer */}
                <div className="mt-12 pt-6 border-t border-neutral-100 text-xs text-neutral-400 text-center">
                    {company.company_name || "Biro Stockholm AB"} · {company.address || "Manhemsvägen 7, 13146 Nacka"} · {company.email || "info@biro.se"}
                </div>
            </div>
        </div>
    );
}
